/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("Stretch", function (context, cls) {
  /**
   * Stretch (x, y)
   * @class Stretch
   * @memberOf classes
   */
  class Stretch /** @lends classes.Stretch.prototype */ {
    // jshint ignore:start
    static undef = {};
    // jshint ignore:end

    /**
     * @constructs
     * @param {Object} [rawOptions] initialization options
     * @param {number} [rawOptions.x] x-stretch to initialize
     * @param {number} [rawOptions.y] y-stretch to initialize
     */
    constructor(rawOptions) {
      const opts = rawOptions || {};
      /**
       * x-stretch value
       * @type {boolean|object}
       */
      this._x = opts.x || cls.Stretch.undef;
      /**
       * y-stretch value
       * @type {boolean|object}
       */
      this._y = opts.y || cls.Stretch.undef;
      /**
       * x-stretch opportunistic value
       * @type {boolean}
       */
      this._opportunisticX = false;
      /**
       * y-stretch opportunistic value
       * @type {boolean}
       */
      this._opportunisticY = false;
      /**
       * x-stretch default value
       * @type {boolean}
       */
      this._defaultX = false;
      /**
       * y-stretch default value
       * @type {boolean}
       */
      this._defaultY = false;
    }

    /**
     * reset values
     */
    reset() {
      this._x = cls.Stretch.undef;
      this._y = cls.Stretch.undef;
      this._opportunisticX = false;
      this._opportunisticY = false;
    }

    /**
     * Returns whether if it has an x-stretch value or not
     * @return {boolean} true if it has an x-stretch value
     */
    isXDefined() {
      return this._x !== cls.Stretch.undef;
    }

    /**
     * Returns whether it has a y-stretch value or not
     * @return {boolean} true if it has a y-stretch value
     */
    isYDefined() {
      return this._y !== cls.Stretch.undef;
    }

    /**
     * Get x-stretch value
     * @param {boolean} [useFallback] return default value if no value
     * @return {*} x-stretch value
     */
    getX(useFallback) {
      if (useFallback && !this.isXDefined()) {
        return this._defaultX;
      }
      return this._x;
    }

    /**
     * Get y-stretch value
     * @param {boolean} [useFallback] return default value if no value
     * @return {*} y-stretch value
     */
    getY(useFallback) {
      if (useFallback && !this.isYDefined()) {
        return this._defaultY;
      }
      return this._y;
    }

    /**
     * Set x-stretch value
     * @param {boolean} x value
     */
    setX(x) {
      if (x === null) {
        this._x = cls.Stretch.undef;
      } else {
        this._x = x;
      }
    }
    /**
     * Set y-stretch value
     * @param {boolean} y value
     */
    setY(y) {
      if (y === null) {
        this._y = cls.Stretch.undef;
      } else {
        this._y = y;
      }
    }

    /**
     * Get x opportunistic stretch value (e.g. would only stretch if other elements sharing stretching dimentsion stretches)
     * @return {boolean} x opportunistic stretch value
     */
    getOpportunisticX() {
      return this._opportunisticX;
    }

    /**
     * Get y opportunistic stretch value (e.g. would only stretch if other elements sharing stretching dimentsion stretches)
     * @return {boolean} y opportunistic stretch value
     */
    getOpportunisticY() {
      return this._opportunisticY;
    }

    /**
     * Set x opportunistic stretch value
     * @param {boolean} x opportunistic stretch value
     */
    setOpportunisticX(x) {
      this._opportunisticX = x;
    }

    /**
     * Set y opportunistic stretch value
     * @param {boolean} y opportunistic stretch value
     */
    setOpportunisticY(y) {
      this._opportunisticY = y;
    }

    /**
     * Set x-stretch default value
     * @param {boolean} x default value
     */
    setDefaultX(x) {
      this._defaultX = x;
    }

    /**
     * Set y-stretch default value
     * @param {boolean} y default value
     */
    setDefaultY(y) {
      this._defaultY = y;
    }

    /**
     * Get x-stretch default value
     * @return {boolean} x default value
     */
    getDefaultX(x) {
      return this._defaultX;
    }

    /**
     * Get y-stretch default value
     * @return {boolean} y default value
     */
    getDefaultY(y) {
      return this._defaultY;
    }
  }
  cls.Stretch = Stretch;
});
